import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { useClassName } from '@travpro/hooks';
import { spring, StaggeredMotion } from 'react-motion';

import FastFactCard from 'components/FastFacts/FastFactCard';

import APIHandler from 'config/APIHandler';

import { useSettings } from 'hooks/useSettings';

/**
 * Configuration object for filtering fast facts by type. Can be empty if no filters are needed. Filter buttons will be
 * displayed only in case of 2+ types.
 *
 * @typedef {object} FilterTypeConfig
 * @property {string} [label] - The display label for the filter type (optional). If not provided, the filter type key
 *   will be used.
 * @property {string} [image] - The URL of an image representing the filter type.
 * @property {string} [imgAlt] - The alt text for the filter type image (optional). If not provided, the filter type key
 *   will be used with '-filter image' appended.
 */

/**
 * Configuration object for defining filter types and their properties.
 * 
 * @type {{ [key: string]: FilterTypeConfig }}
 */
const filterTypes = {
  national: {
    label: 'National',
    image: 'https://front.travpromobile.com/1394/flag_small.jpeg',
    imgAlt: 'flags',
  },
  regional: {
    label: 'Regional',
    image: 'https://front.travpromobile.com/1394/download (1).jpeg',
    imgAlt: 'coast',
  },
};

/**
 * Configuration object for filtering fast facts by area. Can be empty if no filters are needed. Filter buttons will be
 * displayed only in case of 2+ areas.
 *
 * @typedef {object} FilterAreaConfig
 * @property {string} [label] - The display label for the filter area (optional). If not provided, the filter area key
 *   will be used with the first letter capitalized.
 */

/**
 * Configuration object for defining filter areas and their properties.
 *
 * @type {{ [key: string]: FilterAreaConfig }}
 */
const filterAreas = {
  west: 'WEST',
  midwest: 'MIDWEST',
  southwest: 'SOUTHWEST',
  northeast: 'NORTHEAST',
  southeast: 'SOUTHEAST',
  pacific: 'PACIFIC',
};

/**
 * Simple helper function that capitalizes the first letter of a string.
 *
 * @param {string} string - The input string to capitalize.
 * @returns {string} The input string with the first letter capitalized.
 */
const capitalizeFirst = (string) => string.charAt(0).toUpperCase() + string.slice(1);

/**
 * @typedef {object} FastFactsProps Props for the FastFacts component
 * @property {boolean} enableFilter A proptype that enables the filter functionality and render when true. Defaults to false;
 */

/**
 * React component for displaying and filtering fast facts.
 *
 * This component fetches fast facts data, allows users to filter facts by type and area, and displays them in an
 * animated staggered list. You can customize the available filter options by modifying the `filterTypes` and
 * `filterAreas` objects in this component's code.
 *
 * @example
 *   // Usage example:
 *   import FastFacts from 'components/FastFacts';
 *
 *   function App() {
 *     return (
 *       <div>
 *         <FastFacts />
 *       </div>
 *     );
 *   }
 *
 * @param {FastFactsProps} props FastFacts props object
 * @returns {JSX.Element} The rendered FastFacts component.
 */
const FastFacts = ({ enableFilter = false }) => {
  const defaultTypeFilter = Object.keys(filterTypes)[0];
  const defaultAreaFilter = Object.keys(filterAreas)[0];

  const hasMultipleTypes = Object.keys(filterTypes).length > 1;
  const hasMultipleAreas = Object.keys(filterAreas).length > 1;

  const [fastFactsData, setFastFactsData] = useState([]);
  const [selectedFilterType, setSelectedTypeFilter] = useState(defaultTypeFilter);
  const [selectedAreaFilter, setSelectedAreaFilter] = useState(defaultAreaFilter);

  const { appId, lang, loaded: loadedAppId } = useSettings();

  const c = useClassName('fastfacts');

  const handleTypeFilterSelect = (event) => {
    const type = event.currentTarget.getAttribute('data-filter');
    if (type) setSelectedTypeFilter(type);
  };

  const handleAreaFilterSelect = (event) => {
    const area = event.currentTarget.getAttribute('data-area');
    if (area) setSelectedAreaFilter(area);
  };

  useEffect(() => {
    const fetchFastFacts = async () => {
      if (!loadedAppId) return;
      try {
        if (!lang) return;

        const { langFile } = lang;
        const locale = `${langFile.toLowerCase()}_${langFile.replace('PT', 'BR').replace('ES', '41').toUpperCase()}`;
        const res = await APIHandler.getFastFacts(appId, '', locale);

        if (res) setFastFactsData(res);
      } catch (error) {
        console.error('Error fetching fast facts:', error);
      }
    };

    fetchFastFacts();
  }, [appId, lang, loadedAppId]); // Dependency array includes appId only, as storageKeys is presumably a constant and does not need to be watched.

  const renderTypeFilterButtons = () =>
    Object.entries(filterTypes).map(([key, filterType]) => (
      <button
        type="button"
        {...c('filter-button', { selected: selectedFilterType === key })}
        data-filter={key}
        onClick={handleTypeFilterSelect}
        key={key}
      >
        <span>{filterType?.label ?? capitalizeFirst(key)}</span>
        <img
          src={filterType.image}
          {...c('filter-button-image')}
          alt={filterType?.imgAlt ?? `${capitalizeFirst(key)}-filter image`}
        />
      </button>
    ));

  const renderAreaFilterButtons = () => {
    if (selectedFilterType === 'national') {
      // Conditionally render area filter buttons only for regional facts.
      return null;
    }
    return Object.entries(filterAreas).map(([key, label]) => (
      <button
        type="button"
        {...c('area', { selected: selectedAreaFilter === key })}
        onClick={handleAreaFilterSelect}
        data-area={key}
        key={key}
      >
        <span>{label ?? capitalizeFirst(key)}</span>
      </button>
    ));
  };

  const renderFastFacts = () => {
    if (!fastFactsData.length > 0) return null;

    const filteredFastFacts = fastFactsData
      .filter((fact) => {
        // if the filter is disabled, all items are treated as "correct"
        if (!enableFilter) return true;

        const { region_type: regionType } = fact;
        return selectedFilterType?.toLowerCase() === regionType?.toLowerCase();
      })
      .filter((fact) => {
        // if the filter is disabled, all items are treated as "correct"
        if (!enableFilter) return true;

        if (selectedFilterType?.toLowerCase() === 'national') {
          return true;
        }
        return selectedAreaFilter?.toLowerCase() === fact.region?.toLowerCase();
      });

    if (!filteredFastFacts.length > 0) return null;

    const startingValues = Object.keys(filteredFastFacts).map(() => ({
      data: 0,
      pos: 20,
    }));

    const staggeredMotionKey = `${selectedFilterType}-${selectedAreaFilter}`;

    const fastFactsList = (
      <StaggeredMotion
        key={staggeredMotionKey}
        defaultStyles={startingValues}
        styles={(prevInterpolatedStyles) =>
          prevInterpolatedStyles.map((_, i) => ({
            data: spring(i === 0 ? 1 : prevInterpolatedStyles[i - 1].data, { stiffness: 320, damping: 28 }),
            pos: spring(i === 0 ? 0 : prevInterpolatedStyles[i - 1].pos, { stiffness: 320, damping: 28 }),
          }))
        }
      >
        {(styles) => (
          // FIXME: deal with fragment
          // eslint-disable-next-line react/jsx-no-useless-fragment
          <React.Fragment>
            {filteredFastFacts &&
              filteredFastFacts.map((fact, id) => {
                const { url, title, subtitle, image, type, itemID } = fact;

                let newUrl = url;
                let newImage = image;
                if (url.includes(',')) {
                  newUrl = url.split(',');
                  [newImage] = newUrl;
                }

                if (type) {
                  return (
                    <div
                      key={itemID}
                      className="fastfactsCard__Animated"
                      style={{ opacity: styles[id].data, transform: `translateY(${styles[id].pos}%)` }}
                    >
                      <FastFactCard
                        appId={appId}
                        image={newImage} // Ensure to use the right variable for image
                        index={id}
                        key={itemID}
                        subtitle={subtitle}
                        title={title}
                        type={type}
                        url={newUrl}
                        itemID={`${itemID}`}
                      />
                    </div>
                  );
                }
                return null;
              })}
          </React.Fragment>
        )}
      </StaggeredMotion>
    );
    return fastFactsList;
  };

  return (
    <div {...c()}>
      {enableFilter && (hasMultipleTypes ?? hasMultipleAreas) && (
        <div {...c('filter')}>
          {'FILTER BY TYPE:'}
          {hasMultipleTypes && (
            <React.Fragment>
              <div {...c('filter-buttons')}>{renderTypeFilterButtons()}</div>
              <div {...c('line')} />
            </React.Fragment>
          )}
          {hasMultipleAreas && <div {...c('areas')}>{renderAreaFilterButtons()}</div>}
        </div>
      )}
      {renderFastFacts()}
    </div>
  );
};

FastFacts.propTypes = {
  enableFilter: PropTypes.bool,
};

export default FastFacts;
